// Select-list parser: splits the comma-separated select-list region into
// SelectItem records, detects explicit/implicit aliases, populates
// `SelectItem.window` for window functions, and resolves simple column
// references via `resolveIdentifier` from the read-only PRD.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Query Structure Extractor — SELECT.

import type { ParseOptions } from '../../sql-grammar/model';
import { rangeFromOffsets, type LineIndex } from '../../sql-grammar/document/lineIndex';
import { readIdentifier, foldIdentifier } from '../scope/identifiers';
import { resolveIdentifier } from '../identifier/resolveIdentifier';
import type { IdentifierRef, SqlRange } from '../../sql-grammar/model';
import type { SelectItem, IdentifierBinding, ParsedBuffer } from '../types';

import type { SelectExtractCtx } from './extractQueryStructure.select';
import { findTopLevelCommas, scanTopLevelKeywords, trimRange } from './scanner';
import { extractWindowSpec } from './extractQueryStructure.window';

export function parseSelectItems(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): readonly SelectItem[] {
  const trimmed = trimRange(ctx.source, start, end, ctx.options);
  if (trimmed.startOffset >= trimmed.endOffset) return [];
  const commas = findTopLevelCommas(ctx.source, trimmed.startOffset, trimmed.endOffset, ctx.options);
  const out: SelectItem[] = [];
  let s = trimmed.startOffset;
  for (const c of commas) {
    out.push(buildSelectItem(ctx, s, c));
    s = c + 1;
  }
  out.push(buildSelectItem(ctx, s, trimmed.endOffset));
  return out;
}

// eslint-disable-next-line complexity
function buildSelectItem(ctx: SelectExtractCtx, start: number, end: number): SelectItem {
  const { source, options, lineIdx, buffer } = ctx;
  const trimmed = trimRange(source, start, end, options);
  const range: SqlRange = rangeFromOffsets(lineIdx, trimmed.startOffset, trimmed.endOffset);

  const aliasSplit = splitItemExprAlias(source, trimmed.startOffset, trimmed.endOffset, options);
  const expressionText = source.slice(aliasSplit.exprStart, aliasSplit.exprEnd);

  const window = extractWindowSpec(ctx, aliasSplit.exprStart, aliasSplit.exprEnd);
  const resolved = resolveSimpleColumn(buffer, options, source, lineIdx, aliasSplit.exprStart, aliasSplit.exprEnd);

  return {
    range,
    expressionText: expressionText.trim(),
    ...(aliasSplit.alias ? { alias: aliasSplit.alias } : {}),
    ...(resolved ? { resolved } : {}),
    ...(window ? { window } : {}),
  };
}

interface ExprAliasSplit {
  readonly exprStart: number;
  readonly exprEnd: number;
  readonly alias?: string;
}

// Detect `<expr> [AS] <alias>` at the END of [start, end). Returns the
// expression range and the alias value (unquoted text). When no alias is
// found, the whole range is the expression.
// eslint-disable-next-line complexity
function splitItemExprAlias(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
): ExprAliasSplit {
  // 1) Look for a top-level `AS` keyword followed by an identifier.
  const kws = scanTopLevelKeywords(source, start, end, options);
  for (let k = kws.length - 1; k >= 0; k--) {
    if (kws[k].upper === 'AS') {
      let j = kws[k].end;
      while (j < end && /\s/.test(source[j])) j++;
      const id = readIdentifier(source, j, end, options);
      if (id) return { exprStart: start, exprEnd: kws[k].start, alias: id.value };
    }
  }
  // 2) Implicit alias: last identifier preceded by whitespace AND a
  //    "complete" expression token (not by `.`, comma, operator, open-paren).
  let i = end;
  while (i > start && /\s/.test(source[i - 1])) i--;
  if (i === start) return { exprStart: start, exprEnd: end };
  const last = source[i - 1];
  let aliasStart = i;
  let aliasValue: string | null = null;
  if (last === '`' || last === ']' || last === '"') {
    const open = last === '`' ? '`' : last === ']' ? '[' : '"';
    let j = i - 2;
    while (j >= start && source[j] !== open) j--;
    if (j >= start) {
      aliasStart = j;
      aliasValue = source.slice(j + 1, i - 1);
    }
  } else if (/[A-Za-z0-9_$]/.test(last)) {
    let j = i - 1;
    while (j >= start && /[A-Za-z0-9_$]/.test(source[j])) j--;
    aliasStart = j + 1;
    if (!/[A-Za-z_]/.test(source[aliasStart])) return { exprStart: start, exprEnd: end };
    aliasValue = source.slice(aliasStart, i);
    // Reject SQL reserved words at the trailing position (FROM, AS handled
    // above, but defensively skip ASC/DESC/NULLS so ORDER-BY items don't bleed).
    const upper = aliasValue.toUpperCase();
    if (upper === 'ASC' || upper === 'DESC' || upper === 'AS' || upper === 'NULLS' || upper === 'WITH') {
      return { exprStart: start, exprEnd: end };
    }
  } else {
    return { exprStart: start, exprEnd: end };
  }
  // Skip whitespace before alias; check the character before that.
  let p = aliasStart;
  while (p > start && /\s/.test(source[p - 1])) p--;
  if (p === start) return { exprStart: start, exprEnd: end };
  if (p === aliasStart) return { exprStart: start, exprEnd: end };
  const prev = source[p - 1];
  if (prev === '.' || prev === ',' || prev === '(') return { exprStart: start, exprEnd: end };
  if (/[+\-*/=<>!&|^~%]/.test(prev)) return { exprStart: start, exprEnd: end };
  return { exprStart: start, exprEnd: p, alias: aliasValue ?? undefined };
}

// Try to resolve a select-item expression as a simple column reference. Only
// fires when the expression is exactly one identifier or a dotted chain.
// eslint-disable-next-line complexity
function resolveSimpleColumn(
  buffer: ParsedBuffer,
  options: ParseOptions,
  source: string,
  lineIdx: LineIndex,
  start: number,
  end: number,
): IdentifierBinding | undefined {
  let i = start;
  while (i < end && /\s/.test(source[i])) i++;
  const head = readIdentifier(source, i, end, options);
  if (!head) return undefined;
  let p = head.end;
  // Optional `.col` suffix.
  if (source[p] === '.') {
    const tail = readIdentifier(source, p + 1, end, options);
    if (!tail) return undefined;
    p = tail.end;
    // Allow second dot for db.schema.col.
    if (source[p] === '.') {
      const tail2 = readIdentifier(source, p + 1, end, options);
      if (!tail2) return undefined;
      p = tail2.end;
    }
  }
  while (p < end && /\s/.test(source[p])) p++;
  if (p < end) return undefined; // leftover bytes — not a simple column.

  // Read the dotted chain into a QualifiedName-shaped IdentifierRef.
  // Reuse readQualifiedName via a synthetic call.
  const qn = buildIdentifierRef(source, options, lineIdx, i, p);
  if (!qn) return undefined;
  return resolveIdentifier(buffer, qn) ?? undefined;
}

function buildIdentifierRef(
  source: string,
  options: ParseOptions,
  lineIdx: LineIndex,
  start: number,
  end: number,
): IdentifierRef | null {
  const first = readIdentifier(source, start, end, options);
  if (!first) return null;
  const parts = [first];
  let pos = first.end;
  while (pos < end && source[pos] === '.') {
    const next = readIdentifier(source, pos + 1, end, options);
    if (!next) break;
    parts.push(next); pos = next.end;
  }
  const last = parts[parts.length - 1];
  const schema = parts.length >= 2 ? parts[parts.length - 2] : undefined;
  const database = parts.length >= 3 ? parts[parts.length - 3] : undefined;
  return {
    qualified: {
      name: last.value,
      raw: source.slice(start, pos),
      quoted: parts.some((p) => p.quoted),
      foldedName: foldIdentifier(last.value, last.quoted, options),
      ...(schema ? { schema: schema.value, foldedSchema: foldIdentifier(schema.value, schema.quoted, options) } : {}),
      ...(database ? { database: database.value, foldedDatabase: foldIdentifier(database.value, database.quoted, options) } : {}),
    },
    range: rangeFromOffsets(lineIdx, start, pos),
    kind: 'column',
  };
}
